import React, {ReactElement, useEffect, useMemo} from "react"
import moment from "moment"
import {kebabCase, startCase} from "lodash"
import {
	Card,
	CardType,
	getCards,
	GetCardsFilters,
	isBusinessCard,
	isIndividualCard,
	isVirtualCard,
	canCloseCard,
	canResetPin,
	closeCard,
	isCloseable,
	resetCardPin,
	unfreezeCard,
	BusinessCard,
} from "../../resources/card"
import CreatedAt from "../CreatedAt/CreatedAt"
import {usePaging} from "../../hooks/usePaging"
import PagingNavBar from "../PagingNavBar/PagingNavBar"
import {BusinessCustomer, IndividualCustomer} from "../../resources/customer"
import classNames from "classnames"
import {
	useAccessToken,
	useIsOrgOrUnitAdminUser,
	useIsOrgUser,
	useIsUnitAdminOrgAdminBankOperationsOnly,
	useUserType,
} from "../../services/auth"
import {useToasts} from "react-toast-notifications"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {useModal} from "react-modal-hook"
import {FreezeCardModal} from "../AccountCards/FreezeCardModal"
import {ConfirmationModal} from "../ConfirmationModal/ConfirmationModal"
import ReactTooltip from "react-tooltip"
import {hasPermission} from "../../services/permission"
import {
	DataTable,
	DataTableActionHeader,
	DataTableBody,
	DataTableCard,
	DataTableCell,
	DataTableHead,
	DataTableRow,
	TablePending,
} from "../DataTable/DataTable"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {useNavigate} from "react-router-dom"
import {DataTableActionButton, DataTableActionItem} from "../DataTable/DataTableActionButton"
import {useQueryState} from "use-location-state"
import SearchBox from "../SearchBox/SearchBox"
import {CustomerOutreachModal} from "../AccountCards/CustomerOutreachModal/CustomerOutreachModal"
import {AllowedCardsColumns, CardsColumns} from "./Cards.types"
import {Filter} from "../Filter/Filter"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AsyncResult} from "../../types/asyncResult"
import {ErrorDocument, Meta, Resource} from "../../resources/common"
import {Bank, findBanks} from "../../resources/bank"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {findOrgs, Org} from "../../resources/org"
import {OrgName} from "../OrgName/OrgName"
import {BankName} from "../BankName/BankName"
import {DepositAccount, isDepositOrBatchOrFBOAccount} from "../../resources/account"

interface CardsProps {
	limit?: number
	includedColumns: Array<AllowedCardsColumns>
	enableTitle?: boolean
	fullHeight?: boolean
	enableSearch?: boolean
	enableOrgFilter?: boolean
	enableBankFilter?: boolean
	enableCardTypeFilter?: boolean
	enableStatusFilter?: boolean
	refreshToken?: number
	title?: string
}

const cardTypeToFilterOptions: Record<CardType, string> = {
	[CardType.individualDebitCard]: "Individual Debit Card",
	[CardType.businessDebitCard]: "Business Debit Card",
	[CardType.businessCreditCard]: "Business Credit Card",
	[CardType.individualVirtualDebitCard]: "Individual Virtual Debit Card",
	[CardType.businessVirtualDebitCard]: "Business Virtual Debit Card",
	[CardType.businessVirtualCreditCard]: "Business Virtual Credit Card",
}

const cardStatusToFilterOptions: Record<string, string> = {
	Active: "Active",
	Frozen: "Frozen",
	Stolen: "Stolen",
	Lost: "Lost",
	ClosedByCustomer: "Closed By Customer",
	SuspectedFraud: "Suspected Fraud",
	Inactive: "Inactive",
	ActiveForOnlineUse: "Active For Online Use",
}

export function CardStatusComponent({card, className}: {card: Card; className?: string}) {
	const statusText = startCase(card.attributes.status)
	const statusSetBy = (card.attributes as Card["attributes"] & {statusSetBy?: string}).statusSetBy
	const isFrozenWithReason = card.attributes.status === "Frozen" && statusSetBy

	return (
		<div className="is-flex">
			<div
				className={classNames("status-badge", kebabCase(card.attributes.status), className)}
				data-tip={isFrozenWithReason ? `Frozen by ${statusSetBy}` : undefined}
				data-for={isFrozenWithReason ? `card-freeze-reason-tooltip-${card.id}` : undefined}
				data-event={isFrozenWithReason ? "click focus" : undefined}
			>
				{statusText}
			</div>
			{isFrozenWithReason ? (
				<ReactTooltip
					id={`card-freeze-reason-tooltip-${card.id}`}
					place="bottom"
					effect="solid"
					className="tooltip-info"
					globalEventOff="click"
					border={true}
					borderColor="#C4C4C4"
					backgroundColor="#F5F5F5"
					textColor="black"
				>
					{card.attributes.freezeReason}
				</ReactTooltip>
			) : null}
		</div>
	)
}

function CardRow({
	card,
	customer,
	account,
	refresh,
	includedColumns,
	included,
}: {
	card: Card
	customer?: IndividualCustomer | BusinessCustomer
	account?: DepositAccount
	refresh: () => void
	includedColumns: Array<CardsColumns>
	included?: Resource[]
}) {
	const navigate = useNavigate()
	const accessToken = useAccessToken()
	const {addToast} = useToasts()
	const isOrgOrUnitAdminUser = useIsOrgOrUnitAdminUser()
	const isUnitAdminOrgAdminBankOperationsOnly = useIsUnitAdminOrgAdminBankOperationsOnly()
	const [closeState, close] = useAsyncResultIdle(closeCard)
	const [resetPinState, resetPin] = useAsyncResultIdle(resetCardPin)
	const [unfreezeState, unfreeze] = useAsyncResultIdle(unfreezeCard)
	const [showFreeze, hideFreeze] = useModal(() => (
		<FreezeCardModal close={hideFreeze} card={card} onSuccess={refresh} />
	))

	const [showCloseConfirmationModal, hideCloseConfirmationModal] = useModal(
		() => (
			<ConfirmationModal
				title="Close Card"
				close={hideCloseConfirmationModal}
				okButtonText={"Close Card"}
				okButtonClassname={"button is-danger is-outlined"}
				cancelButtonClassname="button is-white"
				onSubmit={() => {
					close(accessToken, card)
				}}
			>
				Are you sure you want to close this card?
			</ConfirmationModal>
		),
		[]
	)

	const [showResetPinConfirmationModal, hideResetPinConfirmationModal] = useModal(
		() => (
			<ConfirmationModal
				title="Reset PIN"
				close={hideResetPinConfirmationModal}
				okButtonText={"Reset PIN"}
				okButtonClassname={"button is-danger is-outlined"}
				cancelButtonClassname="button is-white"
				onSubmit={() => {
					resetPin(accessToken, card)
				}}
			>
				Are you sure you want to reset the PIN for this card?
				<br />
				This is a high-risk action that should only be taken following the card holder&apos;s request, and after the
				process of customer identity verification has been completed
			</ConfirmationModal>
		),
		[]
	)

	const [showCustomerOutreach, hideCustomerOutreach] = useModal(() => (
		<CustomerOutreachModal
			close={hideCustomerOutreach}
			card={card}
			customer={customer as IndividualCustomer | BusinessCustomer}
			onSuccess={refresh}
		/>
	))

	useEffect(() => {
		closeState.match(
			() => null,
			() => null,
			() => refresh(),
			(e) => addToast(e.errors[0].title)
		)
	}, [closeState])

	useEffect(() => {
		unfreezeState.match(
			() => null,
			() => null,
			() => refresh(),
			(e) => addToast(e.errors[0].title)
		)
	}, [unfreezeState])

	useEffect(() => {
		resetPinState.match(
			() => null,
			() => null,
			() => {
				addToast("The PIN has been reset successfully", {appearance: "success"})
				refresh()
			},
			(e) => addToast(e.errors[0].title)
		)
	}, [resetPinState])

	const contentColumns: Partial<Record<AllowedCardsColumns, ReactElement>> = {}

	if (includedColumns.includes(CardsColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell>
				{/* <a
					className="data-table-id link"
					onClick={(e) => {
						e.preventDefault()
						navigate(`/cards/${card.id}`)
					}}
				>
					{card.id}
				</a> */}
				<a className="data-table-id link">{card.id}</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CardsColumns.last4Digits)) {
		contentColumns["Last 4 Digits"] = (
			<DataTableCell>
				{" "}
				<b> {card.attributes.last4Digits} </b>{" "}
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CardsColumns.cardHolder)) {
		contentColumns["Cardholder"] = (() => {
			if (isIndividualCard(card) && customer?.type === "individualCustomer") {
				return (
					<DataTableCell>
						{" "}
						{customer?.attributes.fullName.first} {customer?.attributes.fullName.last}{" "}
					</DataTableCell>
				)
			} else if (isBusinessCard(card) && customer?.type === "businessCustomer") {
				const businessCard = card as BusinessCard
				return (
					<DataTableCell>
						{" "}
						{businessCard.attributes.fullName.first} {businessCard.attributes.fullName.last}{" "}
					</DataTableCell>
				)
			} else {
				return (
					<DataTableCell>
						<span> -- </span>
					</DataTableCell>
				)
			}
		})()
	}

	if (includedColumns.includes(CardsColumns.expiration)) {
		contentColumns["Expiration"] = (
			<DataTableCell>{moment(card.attributes.expirationDate).format("MM/YYYY")}</DataTableCell>
		)
	}

	if (includedColumns.includes(CardsColumns.accountId)) {
		contentColumns["Account"] = (
			<DataTableCell>
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (card.relationships.account.data.id) {
							navigate(`/accounts/${card.relationships.account.data.id}`)
						}
					}}
				>
					{card.relationships.account.data.id}
				</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CardsColumns.status)) {
		contentColumns["Status"] = (
			<DataTableCell>
				<CardStatusComponent card={card} />
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CardsColumns.cardType)) {
		contentColumns["Type"] = <DataTableCell>{isVirtualCard(card) ? "Virtual" : "Physical"}</DataTableCell>
	}

	if (includedColumns.includes(CardsColumns.createdAt)) {
		contentColumns["Created At"] = (
			<DataTableCell>
				<CreatedAt createdAt={card.attributes.createdAt} />
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CardsColumns.updatedAt)) {
		contentColumns["Updated At"] = (
			<DataTableCell>
				{card.attributes.updatedAt ? (
					moment(card.attributes.updatedAt).format("L")
				) : (
					<span className="empty-date-placeholder"> --/--/---- </span>
				)}
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CardsColumns.org)) {
		contentColumns["Org"] =
			account && isDepositOrBatchOrFBOAccount(account) && account.relationships?.org?.data?.id ? (
				<DataTableCell>
					<OrgName orgId={account.relationships.org.data.id.toString()} element="span" included={included} />
				</DataTableCell>
			) : (
				<DataTableCell>
					<span>--</span>
				</DataTableCell>
			)
	}

	if (includedColumns.includes(CardsColumns.bank)) {
		contentColumns["Bank"] =
			account && isDepositOrBatchOrFBOAccount(account) && account.relationships?.bank?.data?.id ? (
				<DataTableCell>
					<BankName bankId={account.relationships.bank.data.id.toString()} included={included} />
				</DataTableCell>
			) : (
				<DataTableCell>
					<span>--</span>
				</DataTableCell>
			)
	}

	if (includedColumns.includes(CardsColumns.actions)) {
		contentColumns["Actions"] = (
			// Freeze card - 1st option if card is active
			<DataTableActionButton enableActions={isCloseable(card)}>
				{(card.attributes.status === "Active" || card.attributes.status === "ActiveForOnlineUse") &&
				isUnitAdminOrgAdminBankOperationsOnly ? (
					<DataTableActionItem title={"Freeze card"} icon={"credit-card-lock"} onClick={() => showFreeze()} />
				) : null}
				{/* Unfreeze card - 1st option if card is frozen */}
				{card.attributes.status === "Frozen" && isUnitAdminOrgAdminBankOperationsOnly ? (
					<DataTableActionItem
						title={"Unfreeze card"}
						icon={"credit-card-unlock"}
						onClick={() => unfreeze(accessToken, card)}
					/>
				) : null}
				{/* Reset PIN - 2nd option */}
				{canResetPin(card) && isOrgOrUnitAdminUser ? (
					<DataTableActionItem
						title={"Reset PIN"}
						icon={"security-password-lock--programing-apps-websites"}
						onClick={() => showResetPinConfirmationModal()}
					/>
				) : null}
				{hasPermission("sendLink.card.unfreeze", "create") && (
					<DataTableActionItem
						title={"Send Customer Outreach"}
						icon={"email-share--emails"}
						onClick={() => showCustomerOutreach()}
					/>
				)}
				{canCloseCard(card) && isUnitAdminOrgAdminBankOperationsOnly ? (
					<DataTableActionItem
						title={"Close card"}
						icon={"credit-card-remove-cross"}
						color="#FF506F"
						onClick={() => showCloseConfirmationModal()}
					/>
				) : null}
			</DataTableActionButton>
		)
	}

	const content: Array<ReactElement | null> = []

	includedColumns.forEach((col, i) => {
		if (col in contentColumns && contentColumns[col]) {
			const column = {...contentColumns[col]} as ReactElement
			column.key = i.toString()
			content.push(column)
		}
	})

	return (
		<DataTableRow className={"clickable-row"} onClick={() => navigate(`/cards/${card.id}`)}>
			{content}
		</DataTableRow>
	)
}

function CardsTable({
	cards,
	customersMap,
	accountsMap,
	hasResults,
	hasPrev,
	hasNext,
	prev,
	next,
	refresh,
	includedColumns,
	sortFunction,
	sortBy,
	fullHeight,
	meta,
	included,
}: {
	cards: Array<Card>
	customersMap: Map<string, IndividualCustomer | BusinessCustomer>
	accountsMap: Map<string, DepositAccount>
	hasResults: boolean
	hasPrev: boolean
	hasNext: boolean
	prev: () => void
	next: () => void
	refresh: () => void
	includedColumns: Array<AllowedCardsColumns>
	sortFunction: () => void
	sortBy: string
	fullHeight?: boolean
	meta?: Meta
	included?: Resource[]
}) {
	return (
		<div className={"cards-table"}>
			<DataTable
				isEmpty={cards.length === 0}
				fullHeight={fullHeight}
				stickyAction={fullHeight}
				noContentText={"No cards found"}
			>
				<DataTableHead>
					<DataTableRow>
						{Object.entries(includedColumns).map((column) => {
							if (column[1] === CardsColumns.createdAt) {
								return (
									<DataTableCell
										clickable
										onClick={() => sortFunction()}
										key={column[1]}
										sortable
										sortApplied={sortBy === "createdAt"}
									>
										{column[1]}
									</DataTableCell>
								)
							}
							return <DataTableCell key={column[0]}>{column[1]}</DataTableCell>
						})}
					</DataTableRow>
				</DataTableHead>
				<DataTableBody>
					{cards.map((card) => (
						<CardRow
							refresh={refresh}
							includedColumns={includedColumns}
							card={card}
							customer={customersMap.get(card.relationships.customer.data.id)}
							account={accountsMap.get(card.relationships.account.data.id)}
							key={card.id}
							included={included}
						/>
					))}
				</DataTableBody>
			</DataTable>
			<PagingNavBar
				meta={meta}
				hasResults={hasResults}
				hasPrev={hasPrev}
				hasNext={hasNext}
				prev={prev}
				next={next}
				isShow={true}
			/>
		</div>
	)
}

export function Cards({
	limit = 25,
	includedColumns,
	enableTitle,
	fullHeight,
	enableSearch,
	enableOrgFilter,
	enableBankFilter,
	enableCardTypeFilter,
	enableStatusFilter,
	refreshToken,
	title = "Cards",
}: CardsProps) {
	const prefix = "card-"
	const userType = useUserType()
	const isOrgUser = useIsOrgUser()
	const showOrgFilter = Boolean(enableOrgFilter) && !isOrgUser
	const showBankFilter = Boolean(enableBankFilter) && !isOrgUser
	const visibleColumns = useMemo(
		() =>
			isOrgUser
				? includedColumns.filter((col) => col !== CardsColumns.org && col !== CardsColumns.bank)
				: includedColumns,
		[includedColumns, isOrgUser]
	)
	const [refreshTokenInternal, refresh] = useRefreshToken()
	const [sortBy, setSortBy] = useQueryState(`${prefix}-sort`, "-createdAt")
	const [query, setQuery] = useQueryState(`${prefix}-filter[query]`, "")
	const [filteredByOrg, setFilteredByOrg] = useQueryState<string[]>(`${prefix}-filter[orgs]`, [])
	const [filteredBanks, setFilteredBanks] = useQueryState<string[]>(`${prefix}-filter[banks]`, [])
	const [cardTypes, setCardTypes] = useQueryState<string[]>(`${prefix}-filter[cardType]`, [])
	const [statuses, setStatuses] = useQueryState<string[]>(`${prefix}-filter[status]`, [])

	const cardTypeFilterOptions = new Map(Object.entries(cardTypeToFilterOptions))
	const statusesOptions = new Map(Object.entries(cardStatusToFilterOptions))

	const accessToken = useAccessToken()

	const banks =
		userType === "unit"
			? useAsyncResult(() => findBanks(accessToken, 0, 10000), [])
			: AsyncResult.pending<Bank[], ErrorDocument>()

	const bankFilter = banks.match(
		() => null,
		(banks) => (
			<Filter
				title="Banks"
				isSearchable
				setStatuses={setFilteredBanks}
				statuses={filteredBanks}
				onFilterFunc={() => reset(limit)}
				options={
					new Map<string, string>(
						banks
							.sort((a, b) => moment(b.attributes.createdAt).diff(moment(a.attributes.createdAt)))
							.map((bank) => [bank.id, bank.attributes.name])
					)
				}
			/>
		),
		(_) => null
	)

	const cardTypeFilter = (
		<Filter
			title="Card Type"
			statuses={cardTypes}
			setStatuses={setCardTypes}
			onFilterFunc={() => reset(limit)}
			options={cardTypeFilterOptions}
		/>
	)

	const statusFilter = (
		<Filter
			statuses={statuses}
			setStatuses={setStatuses}
			onFilterFunc={() => reset(limit)}
			options={statusesOptions}
			title="Status"
		/>
	)

	const orgs =
		(userType === "unit" || userType === "bank") && showOrgFilter
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) => {
			const filters: GetCardsFilters = {
				orgId: isOrgUser ? undefined : filteredByOrg,
				bankId: isOrgUser ? undefined : filteredBanks,
				status: statuses,
				types: cardTypes.map((type) => type.charAt(0).toUpperCase() + type.slice(1)),
				searchQuery: query,
			}
			return getCards(accessToken, offset, limit, filters, sortBy)
		},
		(x) => x.data.length,
		[
			refreshTokenInternal,
			sortBy,
			query,
			filteredByOrg.join(","),
			filteredBanks.join(","),
			cardTypes.join(","),
			statuses.join(","),
			refreshToken,
		].filter(Boolean),
		prefix,
		true
	)

	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredByOrg, filteredByOrg, reset, limit),
		[orgs, setFilteredByOrg, filteredByOrg.join(","), reset, limit]
	)

	const [searchBox, setIsSearchLoading] = SearchBox(
		query,
		"Search Last 4 Digits",
		500,
		onSearch,
		true,
		4,
		4,
		"^\\d{4}$"
	)

	useEffect(() => {
		result.match(
			() => null,
			() => setIsSearchLoading(false),
			() => setIsSearchLoading(false)
		)
	}, [result])

	function toggleSort() {
		setSortBy(sortBy === "-createdAt" ? "createdAt" : "-createdAt")
		reset(limit)
	}

	function onSearch(searchTerm: string) {
		setQuery(searchTerm)
		reset(limit)
	}

	const filters = [
		...(enableCardTypeFilter ? [cardTypeFilter] : []),
		...(showOrgFilter ? [orgFilter] : []),
		...(showBankFilter ? [bankFilter] : []),
		...(enableStatusFilter ? [statusFilter] : []),
	]

	return (
		<DataTableCard className={"cards-card"}>
			<DataTableActionHeader
				enableSticky={fullHeight}
				searchBox={enableSearch ? searchBox : null}
				filters={filters.length === 0 ? null : filters}
				title={enableTitle ? title : null}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={visibleColumns.length} />}
			>
				{({value: cards}) => {
					const customersMap = cards.included
						? new Map(
								cards.included
									.filter((i) => ["individualCustomer", "businessCustomer"].includes(i.type))
									.map((i) => i as IndividualCustomer | BusinessCustomer)
									.map((i) => [i.id, i])
						  )
						: new Map<string, IndividualCustomer | BusinessCustomer>()

					const accountsMap = cards.included
						? new Map(
								cards.included
									.filter((i) => i.type && i.type.toString().toLowerCase().includes("account"))
									.map((i) => i as DepositAccount)
									.map((i) => [i.id, i])
						  )
						: new Map<string, DepositAccount>()

					return (
						<CardsTable
							cards={cards.data}
							customersMap={customersMap}
							accountsMap={accountsMap}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							refresh={refresh}
							includedColumns={visibleColumns}
							sortFunction={toggleSort}
							sortBy={sortBy}
							fullHeight={fullHeight}
							meta={cards.meta}
							included={cards.included}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
